// EOB → ERA pipeline — V2 (/api/edi/eobs-v2). Drop-in replacement for lib/eobEra.js:
// exports the same names/signatures so consumers only swap the import, plus V2 additions.
// V2 differences handled here:
//   - `complete` returns immediately and QUEUES an async job (V1 ran inline).
//   - progress streams via GET /files/heartbeat (latest_progress) + per-file progress{stage,message}, job_status.
//   - ERA is one call POST /files/{id}/generate-era -> { ok, era_text, ... } (no run-validation/acknowledge/download).
//   - mappings live under /mappings/*; readiness via /files/{id}/era-readiness; needs_review gates generation.
import { api } from "@/lib/api";

const BASE = "/api/edi/eobs-v2";
const CHUNK_SIZE = 5 * 1024 * 1024; // 5 MB — safely under the 10 MB server max

export const MAX_FILE_BYTES = 50 * 1024 * 1024; // 50 MB cap
export const ACCEPTED = ".pdf,image/png,image/jpeg,image/jpg";

// Engine is fixed to Lumina Lens Extreme (astra) — shown, not user-selectable.
export const ENGINE = "astra";
export const ENGINE_LABEL = "Lumina Lens Extreme";

export function validateEobFile(file) {
  if (!file) return "No file selected.";
  const okType = /pdf$/i.test(file.type) || /^image\/(png|jpe?g)$/i.test(file.type) ||
    /\.(pdf|png|jpe?g)$/i.test(file.name);
  if (!okType) return "Unsupported file type. Upload a PDF, PNG or JPG.";
  if (file.size > MAX_FILE_BYTES) return "File is too large. Maximum is 50 MB.";
  if (file.size === 0) return "That file is empty.";
  return null;
}

export async function getEngines({ signal } = {}) {
  const { data } = await api.get(`${BASE}/engines`, { signal });
  return data; // { engines:[{engine,label,available,is_default}], default }
}

// Chunked upload. V2 `complete` returns immediately and queues an async job.
export async function uploadEob(file, { onProgress, signal, engine = ENGINE } = {}) {
  const totalChunks = Math.max(1, Math.ceil(file.size / CHUNK_SIZE));

  const initForm = new FormData();
  initForm.append("filename", file.name);
  initForm.append("total_size", String(file.size));
  initForm.append("total_chunks", String(totalChunks));
  if (engine) initForm.append("engine", engine);
  const { data: init } = await api.post(`${BASE}/upload-chunked/init`, initForm, { signal });
  const uploadId = init.upload_id;

  for (let i = 0; i < totalChunks; i++) {
    const start = i * CHUNK_SIZE;
    const blob = file.slice(start, Math.min(start + CHUNK_SIZE, file.size));
    const form = new FormData();
    form.append("chunk_index", String(i));
    form.append("chunk", blob, file.name);
    await api.post(`${BASE}/upload-chunked/${uploadId}/chunk`, form, { signal });
    onProgress?.(Math.round(((i + 1) / totalChunks) * 100));
  }

  const { data: done } = await api.post(`${BASE}/upload-chunked/${uploadId}/complete`, null, { signal });
  return done; // { status, file_id, job_id, filename }  (job is async)
}

export async function listEobFiles({ since = "all", limit = 500, q, signal } = {}) {
  const params = { since, limit };
  if (q) params.q = q;
  const { data } = await api.get(`${BASE}/files`, { params, signal });
  return Array.isArray(data?.files) ? data.files : [];
}

export async function getEobFile(fileId, { signal } = {}) {
  const files = await listEobFiles({ signal });
  return files.find((f) => f.file_id === fileId) || null;
}

// Global pipeline heartbeat — { counts:{queued,processing,processed,failed}, latest_progress }.
export async function getHeartbeat({ signal } = {}) {
  const { data } = await api.get(`${BASE}/files/heartbeat`, { signal });
  return data;
}

export async function getEobAnalysis(fileId, { signal } = {}) {
  const { data } = await api.get(`${BASE}/files/${fileId}/analysis`, { signal });
  return data;
}

export async function getEobPdf(fileId, { signal } = {}) {
  const { data } = await api.get(`${BASE}/files/${fileId}/pdf`, { signal });
  return data;
}

export async function getEobMarkdown(fileId, { signal } = {}) {
  const { data } = await api.get(`${BASE}/files/${fileId}/markdown`, { signal });
  return data;
}

export async function getEobQueueHealth({ signal } = {}) {
  const { data } = await api.get(`${BASE}/queue-health`, { signal });
  return data;
}

// Coverage now carries data_gaps:{failed_files, processed_missing_payer, processed_missing_payee}.
export async function getCoverage({ signal } = {}) {
  const { data } = await api.get(`${BASE}/profiler/coverage`, { signal });
  return data || {};
}

// V1 getMapperGaps() compat — V2 has no per-gap listing; surface coverage.data_gaps counts instead.
export async function getMapperGaps({ signal } = {}) {
  const cov = await getCoverage({ signal }).catch(() => ({}));
  return { gaps: [], totals: cov.data_gaps || {} };
}

export async function deleteEobFile(fileId) {
  await api.delete(`${BASE}/files/${fileId}`);
}

// Mappings (shared masters with V1 — nothing to re-map).
export async function linkPayer(payerName, ediPayerId) {
  await api.post(`${BASE}/mappings/payer`, { payer_name: payerName, edi_payer_id: ediPayerId });
}
export async function linkPayee(payeeName, ehiClientId) {
  await api.post(`${BASE}/mappings/payee-ehi`, { payee_name: payeeName, ehi_client_id: ehiClientId });
}
export async function linkPayeeNpi(payeeName, payeeNpi) {
  await api.post(`${BASE}/mappings/payee-npi`, { payee_name: payeeName, payee_npi: payeeNpi });
}

// Bulk align (Needs-Mapping KPI cards). Masters are shared with V1, so these hit the V1 bulk
// endpoints directly (per the migration guide). Each upserts per-name, skips blanks, returns {saved,errors}.
export async function bulkLinkPayers(links) {
  const { data } = await api.post(`/api/edi/eobs/profiler/payer-links/bulk`, { links });
  return data || {};
}
export async function bulkLinkPayeesEhi(links) {
  const { data } = await api.post(`/api/edi/eobs/profiler/payee-links/bulk`, { links });
  return data || {};
}
export async function bulkLinkPayeeNpis(links) {
  const { data } = await api.post(`/api/edi/eobs/profiler/payee-npi-links/bulk`, { links });
  return data || {};
}

// Per-file mapping readiness (EDI Payer ID / Payee NPI / EHI Client ID) → { ready, ... }.
export async function getEraReadiness(fileId, { signal } = {}) {
  const { data } = await api.get(`${BASE}/files/${fileId}/era-readiness`, { signal });
  return data;
}

// Single call — generates & returns the 835. { ok, era_text, filename, balance, segment_count, structural_validation }
// ok:false with error:"mapping_incomplete" | "structural_invalid" (+ structural_errors[]).
export async function generateEra(fileId) {
  try {
    const { data } = await api.post(`${BASE}/files/${fileId}/generate-era`);
    return data;
  } catch (err) {
    const d = err?.response?.data;
    if (d && typeof d === "object") return { ok: false, ...d };
    throw err;
  }
}

// Call after the user saves the .era client-side.
export async function markEraDownloaded(fileId) {
  await api.post(`${BASE}/files/${fileId}/mark-era-downloaded`).catch(() => {});
}

// ---- V1-compatibility shims so existing flow code keeps working with minimal edits ----

// Distil readiness failures into the V1 runEraValidation() shape { generated, failures[], status }.
function readinessFailures(readiness) {
  const out = [];
  const checks = readiness?.checks || readiness?.requirements || readiness?.mappings || [];
  (Array.isArray(checks) ? checks : []).forEach((c) => {
    if (c && (c.ok === false || c.ready === false || c.satisfied === false)) {
      out.push({ code: c.code || c.key || "MAPPING", reason: c.reason || c.message || c.label || "Mapping required." });
    }
  });
  return out;
}

// V1 compat: check readiness (no separate validation step in V2).
export async function runEraValidation(fileId) {
  const r = await getEraReadiness(fileId).catch(() => null);
  const failures = readinessFailures(r);
  const ready = r ? (r.ready === true && failures.length === 0) : false;
  return { generated: ready ? 1 : 0, failures, status: ready ? "ready" : "blocked" };
}

// V1 compat: no acknowledge step in V2.
export async function acknowledgeReconciliation() { /* no-op in V2 */ }

// V1 compat: getEra(fileId) → generate the 835 and return { available, text, failures }.
export async function getEra(fileId) {
  const res = await generateEra(fileId).catch(() => ({ ok: false }));
  if (res && res.ok && res.era_text) return { available: true, text: res.era_text, filename: res.filename, meta: res };
  const failures = Array.isArray(res?.structural_errors)
    ? res.structural_errors.map((e) => ({ code: "STRUCTURAL", reason: typeof e === "string" ? e : (e.message || JSON.stringify(e)) }))
    : (res?.error ? [{ code: String(res.error).toUpperCase(), reason: res.detail || res.message || String(res.error) }] : []);
  return { available: false, text: "", failures, meta: res };
}

// Line / balancing controls (new in V2).
export async function manualAdjustment(fileId, body) {
  const { data } = await api.post(`${BASE}/files/${fileId}/manual-adjustment`, body);
  return data;
}
export async function deleteManualAdjustment(fileId, adjId) {
  const { data } = await api.delete(`${BASE}/files/${fileId}/manual-adjustment/${adjId}`);
  return data;
}
export async function editLine(fileId, ci, li, body) {
  const { data } = await api.put(`${BASE}/files/${fileId}/claims/${ci}/lines/${li}/edit`, body);
  return data;
}
export async function autoBalance(fileId, enabled) {
  const { data } = await api.post(`${BASE}/files/${fileId}/auto-balance`, { enabled });
  return data;
}

// Deep-recovery was removed in V2 (extraction retries are automatic). Compat no-ops so
// consumers that still import these don't break; they resolve to a benign shape.
export async function runDeepRecovery() { return { ok: false, unsupported: true }; }
export async function getDeepRecoveryStatus() { return { status: "unsupported" }; }

// ---- extraction failure helpers (unchanged from V1) ----
const TOKEN_LIMIT_RE = /token limit|TOKEN_LIMIT_EXCEEDED|Free Tier limit/i;

export function extractionFailureReason(analysis, fileEntry) {
  const ex = analysis?.extraction || {};
  const rawErr = fileEntry?.error ? String(fileEntry.error) : "";
  if (TOKEN_LIMIT_RE.test(rawErr) || TOKEN_LIMIT_RE.test(JSON.stringify(ex || {}))) {
    return "The upstream document-AI service has hit its monthly token limit, so this EOB couldn't be analysed. This is an account/billing limit on the analysis service — not a problem with your document. It resets monthly (or the plan can be upgraded). Note & claim coding draw on the same quota and may be affected until it resets.";
  }
  if (Array.isArray(ex.extraction_warnings) && ex.extraction_warnings.length) return ex.extraction_warnings.join(" ");
  if (ex.error_reason) return String(ex.error_reason).replace(/_/g, " ");
  if (rawErr) {
    const m = rawErr.match(/['"]message['"]:\s*["']([^"']+)["']/);
    return m ? m[1].split("\n")[0] : rawErr.slice(0, 240);
  }
  return "Lens couldn't extract this document.";
}

export function isExtractionFailed(analysis, fileEntry) {
  if (analysis?.extraction?.extraction_failed) return true;
  if (fileEntry?.error && TOKEN_LIMIT_RE.test(String(fileEntry.error))) return true;
  const s = fileEntry?.status;
  const j = fileEntry?.job_status;
  return s === "failed" || s === "error" || j === "failed";
}
